import { type LoaderFunctionArgs, json } from '@remix-run/node'
import { useLoaderData } from '@remix-run/react'
import { Button } from '#app/components/buttons/button.tsx'
import { Flex } from '#app/components/layout/flex.tsx'
import { VisuallyHidden } from '#app/components/layout/visually-hidden.tsx'
import { Icon } from '#app/components/media/icon.tsx'
import { Dialog, DialogTitle, DialogTrigger } from '#app/components/overlays/dialog.tsx'
import { A } from '#app/components/typography/a.tsx'
import { Code } from '#app/components/typography/code.tsx'
import { H2 } from '#app/components/typography/h2.tsx'
import { P } from '#app/components/typography/p.tsx'
import { sessionKey } from '#app/utils/auth.server.ts'
import { prisma } from '#app/utils/db.server.ts'
import { authSessionStorage } from '#app/utils/session.server.ts'
import { readSource } from '../sections/sections.server.tsx'
import { Description, Features, Source, type componentProps, Usage, Keyboard, Styling, Parts } from '../sections/sections.tsx'

export const handle = {
	breadcrumb: 'Dialog',
}

const component: componentProps = {
	name: 'Dialog',
	shortName: 'Dialog',
	fileName: 'dialog',
	category: 'overlays',
	description: [
		'An overlay shown above other content in the application.',
		'The HTML <dialog> element can be used to build dialogs. However, it is not yet widely supported across browsers, and building fully accessible custom dialogs from scratch is very difficult and error prone. Dialog helps achieve accessible dialogs that can be styled as needed.',
	],
	features: [
		'Can be dialog or alert dialog.',
		'Opens at the center (default), left, right, top, or bottom of the screen.',
		'Focus is moved into the dialog on mount, and restored to the trigger element on unmount.',
		'While open, focus is contained within the dialog, preventing the user from tabbing outside.',
		'Exposed to assistive technology as a dialog with ARIA.',
		'The dialog is automatically labeled by a nested DialogTitle element.',
		'Content outside the dialog is hidden from assistive technologies while it is open.',
		'Esc closes the component automatically.',
	],
	keyboard: [
		{ key: 'Space', description: 'Opens/closes the dialog.' },
		{ key: 'Enter', description: 'Opens/closes the dialog.' },
		{ key: 'Tab', description: 'Moves focus to the next focusable element.' },
		{ key: 'Shift + Tab', description: 'Moves focus to the previous focusable element.' },
		{ key: 'Esc', description: 'Closes the dialog and moves focus to the DialogTrigger.' },
	],
	parts: [
		{
			name: 'DialogTrigger',
			description: 'The DialogTrigger component does not render any DOM elements. It only passes through its children.',
			props: [
				{ name: 'children *', type: 'ReactNode', default: '', description: 'DialogTrigger needs at least two children: a <Button> and a <Dialog>.' },
				{ name: 'isOpen', type: 'boolean', default: '', description: 'Whether the overlay is open by default (controlled).' },
				{ name: 'defaultOpen', type: 'boolean', default: '', description: 'Whether the overlay is open by default (uncontrolled).' },
			],
			events: [{ name: 'onOpenChange', type: '(isOpen: boolean) => void', description: "Handler that is called when the overlay's open state changes." }],
		},
		{
			name: 'Dialog',
			description: 'A dialog is an overlay shown above other content in an application.',
			props: [
				{
					name: 'alert',
					type: 'boolean',
					default: 'false',
					description: 'Whether the dialog is an alert dialog.',
				},
				{
					name: 'side',
					type: "'center' | 'left' | 'right' | 'top' | 'bottom'",
					default: "'center'",
					description: 'Side of the screen the dialog will open from.',
				},
				{
					name: 'children',
					type: 'ReactNode | (opts: DialogRenderProps) => ReactNode',
					default: '',
					description: 'Children of the dialog. A function may be provided to access a function to close the dialog.',
				},
				{
					name: 'className',
					type: 'string | (values: ButtonRenderProps) => string',
					default: '',
					description: 'The CSS className for the element. A function may be provided to compute the class based on component state.',
				},
				{ name: 'style', type: 'CSSProperties', default: '', description: 'The inline style for the element.' },
			],
			accessibility: [
				{ name: 'id', type: 'string', description: "The element's unique identifier." },
				{ name: 'aria-label', type: 'string', description: 'Defines a string value that labels the current element.' },
				{ name: 'aria-labelledby', type: 'string', description: 'Identifies the element (or elements) that labels the current element.' },
				{ name: 'aria-describedby', type: 'string', description: 'Identifies the element (or elements) that describes the object.' },
				{ name: 'aria-details', type: 'string', description: 'Identifies the element (or elements) that provide a detailed, extended description for the object.' },
			],
		},
		{
			name: 'DialogTitle',
			description: 'Contains the title of the dialog.',
		},
	],
	usage: `<DialogTrigger>
	<Button variant="primary">Create new post</Button>
	<Dialog>
		{({ close }) => (
			<>
				<VisuallyHidden>
					<DialogTitle>What is happening?!</DialogTitle>
				</VisuallyHidden>
				<div className="flex items-start space-x-4">
					<div className="flex-shrink-0">
						<img
							className="inline-block h-10 w-10 rounded-full"
							src="https://images.unsplash.com/photo-1550525811-e5869dd03032?ixlib=rb-1.2.1&ixid=eyJhcHBfaWQiOjEyMDd9&auto=format&fit=facearea&facepad=2&w=256&h=256&q=80"
							alt=""
						/>
					</div>
					<div className="min-w-0 flex-1">
						<div className="border-b border-muted-200 focus-within:border-brand">
							<label htmlFor="comment" className="sr-only">
								What is happening?!
							</label>
							<textarea
								rows={3}
								name="comment"
								id="comment"
								className="block w-full resize-none border-0 border-b border-transparent p-0 pb-2 text-muted-900 outline-none placeholder:text-muted-400 focus:border-brand focus:ring-0 sm:text-sm sm:leading-6"
								placeholder="What is happening?!"
								defaultValue={''}
							/>
						</div>
						<div className="flex justify-between pt-2">
							<div className="flex items-center">
								<div className="flow-root">
									<Button variant="ghost" size="sm" type="button" className="text-muted-400">
										<Icon name="image" className="h-5 w-5" />
										<span className="sr-only">Attach an image</span>
									</Button>
									<Button variant="ghost" size="sm" type="button" className="text-muted-400">
										<Icon name="calendar" className="h-5 w-5" />
										<span className="sr-only">Schedule</span>
									</Button>
								</div>
							</div>
							<div className="flex-shrink-0">
								<Button type="submit" onPress={close}>
									Post
								</Button>
							</div>
						</div>
					</div>
				</div>
			</>
		)}
	</Dialog>
</DialogTrigger>`,
	styling: `/* ### Modals ### */
@layer components {
	/* Dialog */
	.dialog {
		@apply relative outline-none;
	}
	.dialog-title {
		@apply my-0 text-size-2xl font-semibold leading-6 text-muted-700;
	}
}`,
}

export const loader = async ({ request }: LoaderFunctionArgs) => {
	const authSession = await authSessionStorage.getSession(request.headers.get('cookie'))
	const sessionId = authSession.get(sessionKey)
	const session =
		sessionId ?
			await prisma.session.findUnique({
				select: { user: { select: { id: true, paid: true } } },
				where: { id: sessionId, expirationDate: { gt: new Date() } },
			})
		:	null
	const source = session?.user.paid ? await readSource({ category: component.category, fileName: component.fileName }) : null
	return json({ source })
}

const DialogRoute = () => {
	const { source } = useLoaderData<typeof loader>()
	const { name, description, features, keyboard, parts, usage, shortName, fileName, styling } = component

	return (
		<>
			<Description name={name} description={description} />
			<Features features={features} />
			<H2>To do</H2>
			<ul className="text-muted-500 dark:text-muted-400 list-inside space-y-1">
				<li className="flex">
					<div>
						<Icon name="arrow-right" size="md" className="mr-2 inline-block" />
					</div>
					<div>
						<a href="https://github.com/adobe/react-spectrum/discussions/5981#discussioncomment-8681109" target="_blank" rel="noreferrer">
							Non-modal dialogs
						</a>
					</div>
				</li>
				<li className="flex">
					<div>
						<Icon name="arrow-right" size="md" className="mr-2 inline-block" />
					</div>
					<div>
						<a href="https://github.com/adobe/react-spectrum/issues/6012" target="_blank" rel="noreferrer">
							Fix animation when element inside Dialog has focus
						</a>
					</div>
				</li>
			</ul>
			<Keyboard keyboard={keyboard} />
			<Parts parts={parts} />
			<P>
				The <Code>{'<DialogTitle>'}</Code> has the same props as an{' '}
				<A href="/components/typography/h2">
					<Code>{'<H2>'}</Code>
				</A>{' '}
				and is mandatory so assistive technology announces the dialog correctly.
			</P>
			<P>
				If you want to visually hide the title, wrap it with <Code>{'<VisuallyHidden>'}</Code>.
			</P>
			<Usage usage={usage} shortName={shortName} />
			<DialogTrigger>
				<Button variant="primary">Create new post</Button>
				<Dialog>
					{({ close }) => (
						<>
							<VisuallyHidden>
								<DialogTitle>What is happening?!</DialogTitle>
							</VisuallyHidden>
							<div className="flex items-start space-x-4">
								<div className="flex-shrink-0">
									<img
										className="inline-block h-10 w-10 rounded-full"
										src="https://images.unsplash.com/photo-1550525811-e5869dd03032?ixlib=rb-1.2.1&ixid=eyJhcHBfaWQiOjEyMDd9&auto=format&fit=facearea&facepad=2&w=256&h=256&q=80"
										alt=""
									/>
								</div>
								<div className="min-w-0 flex-1">
									<div className="border-muted-200 focus-within:border-brand border-b">
										<label htmlFor="comment" className="sr-only">
											What is happening?!
										</label>
										<textarea
											rows={3}
											name="comment"
											id="comment"
											className="text-muted-900 placeholder:text-muted-400 focus:border-brand block w-full resize-none border-0 border-b border-transparent p-0 pb-2 outline-none focus:ring-0 sm:text-sm sm:leading-6"
											placeholder="What is happening?!"
											defaultValue={''}
										/>
									</div>
									<div className="flex justify-between pt-2">
										<div className="flex items-center">
											<div className="flow-root">
												<Button variant="ghost" size="sm" type="button" className="text-muted-400">
													<Icon name="image" className="h-5 w-5" />
													<span className="sr-only">Attach an image</span>
												</Button>
												<Button variant="ghost" size="sm" type="button" className="text-muted-400">
													<Icon name="calendar" className="h-5 w-5" />
													<span className="sr-only">Schedule</span>
												</Button>
											</div>
										</div>
										<div className="flex-shrink-0">
											<Button type="submit" onPress={close}>
												Post
											</Button>
										</div>
									</div>
								</div>
							</div>
						</>
					)}
				</Dialog>
			</DialogTrigger>
			<P>And here are all the variantions of the dialog.</P>
			<Flex orientation="horizontal" gap="6">
				<Flex orientation="vertical" gap="6">
					<DialogTrigger>
						<Button variant="primary">Dialog, center</Button>
						<Dialog side="center">
							{({ close }) => (
								<>
									<VisuallyHidden>
										<DialogTitle>What is happening?!</DialogTitle>
									</VisuallyHidden>
									<div className="flex items-start space-x-4">
										<div className="flex-shrink-0">
											<img
												className="inline-block h-10 w-10 rounded-full"
												src="https://images.unsplash.com/photo-1550525811-e5869dd03032?ixlib=rb-1.2.1&ixid=eyJhcHBfaWQiOjEyMDd9&auto=format&fit=facearea&facepad=2&w=256&h=256&q=80"
												alt=""
											/>
										</div>
										<div className="min-w-0 flex-1">
											<div className="border-muted-200 focus-within:border-brand border-b">
												<label htmlFor="comment" className="sr-only">
													What is happening?!
												</label>
												<textarea
													rows={3}
													name="comment"
													id="comment"
													className="text-muted-900 placeholder:text-muted-400 focus:border-brand block w-full resize-none border-0 border-b border-transparent p-0 pb-2 outline-none focus:ring-0 sm:text-sm sm:leading-6"
													placeholder="What is happening?!"
													defaultValue={''}
												/>
											</div>
											<div className="flex justify-between pt-2">
												<div className="flex items-center">
													<div className="flow-root">
														<Button variant="ghost" size="sm" type="button" className="text-muted-400">
															<Icon name="image" className="h-5 w-5" />
															<span className="sr-only">Attach an image</span>
														</Button>
														<Button variant="ghost" size="sm" type="button" className="text-muted-400">
															<Icon name="calendar" className="h-5 w-5" />
															<span className="sr-only">Schedule</span>
														</Button>
													</div>
												</div>
												<div className="flex-shrink-0">
													<Button type="submit" onPress={close}>
														Post
													</Button>
												</div>
											</div>
										</div>
									</div>
								</>
							)}
						</Dialog>
					</DialogTrigger>
					<DialogTrigger>
						<Button variant="primary">Dialog, left</Button>
						<Dialog side="left">
							{({ close }) => (
								<>
									<VisuallyHidden>
										<DialogTitle>What is happening?!</DialogTitle>
									</VisuallyHidden>
									<div className="flex items-start space-x-4">
										<div className="flex-shrink-0">
											<img
												className="inline-block h-10 w-10 rounded-full"
												src="https://images.unsplash.com/photo-1550525811-e5869dd03032?ixlib=rb-1.2.1&ixid=eyJhcHBfaWQiOjEyMDd9&auto=format&fit=facearea&facepad=2&w=256&h=256&q=80"
												alt=""
											/>
										</div>
										<div className="min-w-0 flex-1">
											<div className="border-muted-200 focus-within:border-brand border-b">
												<label htmlFor="comment" className="sr-only">
													What is happening?!
												</label>
												<textarea
													rows={3}
													name="comment"
													id="comment"
													className="text-muted-900 placeholder:text-muted-400 focus:border-brand block w-full resize-none border-0 border-b border-transparent p-0 pb-2 outline-none focus:ring-0 sm:text-sm sm:leading-6"
													placeholder="What is happening?!"
													defaultValue={''}
												/>
											</div>
											<div className="flex justify-between pt-2">
												<div className="flex items-center">
													<div className="flow-root">
														<Button variant="ghost" size="sm" type="button" className="text-muted-400">
															<Icon name="image" className="h-5 w-5" />
															<span className="sr-only">Attach an image</span>
														</Button>
														<Button variant="ghost" size="sm" type="button" className="text-muted-400">
															<Icon name="calendar" className="h-5 w-5" />
															<span className="sr-only">Schedule</span>
														</Button>
													</div>
												</div>
												<div className="flex-shrink-0">
													<Button type="submit" onPress={close}>
														Post
													</Button>
												</div>
											</div>
										</div>
									</div>
								</>
							)}
						</Dialog>
					</DialogTrigger>
					<DialogTrigger>
						<Button variant="primary">Dialog, right</Button>
						<Dialog side="right">
							{({ close }) => (
								<>
									<VisuallyHidden>
										<DialogTitle>What is happening?!</DialogTitle>
									</VisuallyHidden>
									<div className="flex items-start space-x-4">
										<div className="flex-shrink-0">
											<img
												className="inline-block h-10 w-10 rounded-full"
												src="https://images.unsplash.com/photo-1550525811-e5869dd03032?ixlib=rb-1.2.1&ixid=eyJhcHBfaWQiOjEyMDd9&auto=format&fit=facearea&facepad=2&w=256&h=256&q=80"
												alt=""
											/>
										</div>
										<div className="min-w-0 flex-1">
											<div className="border-muted-200 focus-within:border-brand border-b">
												<label htmlFor="comment" className="sr-only">
													What is happening?!
												</label>
												<textarea
													rows={3}
													name="comment"
													id="comment"
													className="text-muted-900 placeholder:text-muted-400 focus:border-brand block w-full resize-none border-0 border-b border-transparent p-0 pb-2 outline-none focus:ring-0 sm:text-sm sm:leading-6"
													placeholder="What is happening?!"
													defaultValue={''}
												/>
											</div>
											<div className="flex justify-between pt-2">
												<div className="flex items-center">
													<div className="flow-root">
														<Button variant="ghost" size="sm" type="button" className="text-muted-400">
															<Icon name="image" className="h-5 w-5" />
															<span className="sr-only">Attach an image</span>
														</Button>
														<Button variant="ghost" size="sm" type="button" className="text-muted-400">
															<Icon name="calendar" className="h-5 w-5" />
															<span className="sr-only">Schedule</span>
														</Button>
													</div>
												</div>
												<div className="flex-shrink-0">
													<Button type="submit" onPress={close}>
														Post
													</Button>
												</div>
											</div>
										</div>
									</div>
								</>
							)}
						</Dialog>
					</DialogTrigger>
					<DialogTrigger>
						<Button variant="primary">Dialog, top</Button>
						<Dialog side="top">
							{({ close }) => (
								<>
									<VisuallyHidden>
										<DialogTitle>What is happening?!</DialogTitle>
									</VisuallyHidden>
									<div className="flex items-start space-x-4">
										<div className="flex-shrink-0">
											<img
												className="inline-block h-10 w-10 rounded-full"
												src="https://images.unsplash.com/photo-1550525811-e5869dd03032?ixlib=rb-1.2.1&ixid=eyJhcHBfaWQiOjEyMDd9&auto=format&fit=facearea&facepad=2&w=256&h=256&q=80"
												alt=""
											/>
										</div>
										<div className="min-w-0 flex-1">
											<div className="border-muted-200 focus-within:border-brand border-b">
												<label htmlFor="comment" className="sr-only">
													What is happening?!
												</label>
												<textarea
													rows={3}
													name="comment"
													id="comment"
													className="text-muted-900 placeholder:text-muted-400 focus:border-brand block w-full resize-none border-0 border-b border-transparent p-0 pb-2 outline-none focus:ring-0 sm:text-sm sm:leading-6"
													placeholder="What is happening?!"
													defaultValue={''}
												/>
											</div>
											<div className="flex justify-between pt-2">
												<div className="flex items-center">
													<div className="flow-root">
														<Button variant="ghost" size="sm" type="button" className="text-muted-400">
															<Icon name="image" className="h-5 w-5" />
															<span className="sr-only">Attach an image</span>
														</Button>
														<Button variant="ghost" size="sm" type="button" className="text-muted-400">
															<Icon name="calendar" className="h-5 w-5" />
															<span className="sr-only">Schedule</span>
														</Button>
													</div>
												</div>
												<div className="flex-shrink-0">
													<Button type="submit" onPress={close}>
														Post
													</Button>
												</div>
											</div>
										</div>
									</div>
								</>
							)}
						</Dialog>
					</DialogTrigger>
					<DialogTrigger>
						<Button variant="primary">Dialog, bottom</Button>
						<Dialog side="bottom">
							{({ close }) => (
								<>
									<VisuallyHidden>
										<DialogTitle>What is happening?!</DialogTitle>
									</VisuallyHidden>
									<div className="flex items-start space-x-4">
										<div className="flex-shrink-0">
											<img
												className="inline-block h-10 w-10 rounded-full"
												src="https://images.unsplash.com/photo-1550525811-e5869dd03032?ixlib=rb-1.2.1&ixid=eyJhcHBfaWQiOjEyMDd9&auto=format&fit=facearea&facepad=2&w=256&h=256&q=80"
												alt=""
											/>
										</div>
										<div className="min-w-0 flex-1">
											<div className="border-muted-200 focus-within:border-brand border-b">
												<label htmlFor="comment" className="sr-only">
													What is happening?!
												</label>
												<textarea
													rows={3}
													name="comment"
													id="comment"
													className="text-muted-900 placeholder:text-muted-400 focus:border-brand block w-full resize-none border-0 border-b border-transparent p-0 pb-2 outline-none focus:ring-0 sm:text-sm sm:leading-6"
													placeholder="What is happening?!"
													defaultValue={''}
												/>
											</div>
											<div className="flex justify-between pt-2">
												<div className="flex items-center">
													<div className="flow-root">
														<Button variant="ghost" size="sm" type="button" className="text-muted-400">
															<Icon name="image" className="h-5 w-5" />
															<span className="sr-only">Attach an image</span>
														</Button>
														<Button variant="ghost" size="sm" type="button" className="text-muted-400">
															<Icon name="calendar" className="h-5 w-5" />
															<span className="sr-only">Schedule</span>
														</Button>
													</div>
												</div>
												<div className="flex-shrink-0">
													<Button type="submit" onPress={close}>
														Post
													</Button>
												</div>
											</div>
										</div>
									</div>
								</>
							)}
						</Dialog>
					</DialogTrigger>
				</Flex>
				<Flex orientation="vertical" gap="6">
					<DialogTrigger>
						<Button variant="danger">Alert dialog, center</Button>
						<Dialog alert side="center">
							{({ close }) => (
								<>
									<DialogTitle>Delete folder (center)</DialogTitle>
									<p className="text-muted-500 mt-3">Are you sure you want to delete "Documents"? All contents will be permanently destroyed.</p>
									<div className="mt-6 flex justify-end gap-2">
										<Button variant="secondary" onPress={close} autoFocus>
											Cancel
										</Button>
										<Button variant="danger" onPress={close}>
											Delete
										</Button>
									</div>
								</>
							)}
						</Dialog>
					</DialogTrigger>
					<DialogTrigger>
						<Button variant="danger">Alert dialog, left</Button>
						<Dialog alert side="left">
							{({ close }) => (
								<>
									<DialogTitle>Delete folder (center)</DialogTitle>
									<p className="text-muted-500 mt-3">Are you sure you want to delete "Documents"? All contents will be permanently destroyed.</p>
									<div className="mt-6 flex justify-end gap-2">
										<Button variant="secondary" onPress={close} autoFocus>
											Cancel
										</Button>
										<Button variant="danger" onPress={close}>
											Delete
										</Button>
									</div>
								</>
							)}
						</Dialog>
					</DialogTrigger>
					<DialogTrigger>
						<Button variant="danger">Alert dialog, right</Button>
						<Dialog alert side="right">
							{({ close }) => (
								<>
									<DialogTitle>Delete folder (center)</DialogTitle>
									<p className="text-muted-500 mt-3">Are you sure you want to delete "Documents"? All contents will be permanently destroyed.</p>
									<div className="mt-6 flex justify-end gap-2">
										<Button variant="secondary" onPress={close} autoFocus>
											Cancel
										</Button>
										<Button variant="danger" onPress={close}>
											Delete
										</Button>
									</div>
								</>
							)}
						</Dialog>
					</DialogTrigger>
					<DialogTrigger>
						<Button variant="danger">Alert dialog, top</Button>
						<Dialog alert side="top">
							{({ close }) => (
								<>
									<DialogTitle>Delete folder (center)</DialogTitle>
									<p className="text-muted-500 mt-3">Are you sure you want to delete "Documents"? All contents will be permanently destroyed.</p>
									<div className="mt-6 flex justify-end gap-2">
										<Button variant="secondary" onPress={close} autoFocus>
											Cancel
										</Button>
										<Button variant="danger" onPress={close}>
											Delete
										</Button>
									</div>
								</>
							)}
						</Dialog>
					</DialogTrigger>
					<DialogTrigger>
						<Button variant="danger">Alert dialog, bottom</Button>
						<Dialog alert side="bottom">
							{({ close }) => (
								<>
									<DialogTitle>Delete folder (center)</DialogTitle>
									<p className="text-muted-500 mt-3">Are you sure you want to delete "Documents"? All contents will be permanently destroyed.</p>
									<div className="mt-6 flex justify-end gap-2">
										<Button variant="secondary" onPress={close} autoFocus>
											Cancel
										</Button>
										<Button variant="danger" onPress={close}>
											Delete
										</Button>
									</div>
								</>
							)}
						</Dialog>
					</DialogTrigger>
				</Flex>
			</Flex>
			<H2>Notes on an alert dialog</H2>
			<P>An alert dialog should be used when the user can't continue without addressing the dialog. He can choose an option or press Escape, but he can't click away.</P>
			<P>When the dialog opens, make sure the Cancel button has focus to avoid accidental actions.</P>
			<P>Also don't label the cancellation button with the text "Close"; use "Cancel/Undo/etc" to make it clear for the user that the confirmation has been denied.</P>
			<Source source={source} fileName={fileName} />
			<Styling styling={styling} />
		</>
	)
}
export default DialogRoute
